import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignService } from 'src/app/private/services/campaign.service';
import { ContactGrouopService } from 'src/app/private/services/contact-groups.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Chart, registerables, Plugin } from 'chart.js';
import {
  campaignDonutChartOptions,
  createCampaignDonutLegendPlugin
} from '../shared/campaign-donut-chart.util';
import { getCampaignEventTypeBadgeClass } from 'src/app/utils/status-badge.util';

Chart.register(...registerables);

const barValueLabelPlugin: Plugin = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const isHorizontal = chart.options.indexAxis === 'y';

    ctx.save();
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.fillStyle = '#334155';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((element, index) => {
        const value = dataset.data[index] as number;
        if (!value) return;

        const { x, y } = element.getProps(['x', 'y'], true);
        if (isHorizontal) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(value), x + 6, y);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(String(value), x, y - 4);
        }
      });
    });

    ctx.restore();
  }
};

Chart.register(barValueLabelPlugin);

// Interfaces
interface TrackingEvent {
  id: string;
  campaign_id: string;
  client_id: string;
  message_id: string;
  email_to: string;
  email_to_name: string | null;
  email_from: string;
  event_type: string;
  status: string;
  bounce_type: string | null;
  bounce_sub_type: string | null;
  complaint_feedback_type: string | null;
  timestamp: string;
  raw_event_data: string;
  createdAt: string;
  updatedAt: string;
}

interface CampaignStatistics {
  total: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  opened: number;
  clicked: number;
  rejected: number;
  // Interest tracking
  interested_count: number;
  not_interested_count: number;
  engaged_count: number;
  total_engaged: number;
}


interface CampaignMetadata {
  sent: number;
  total: number;
  failed: number;
  queued: number;
  message: string;
  suppressed: number;
  frequencySkipped: number;
}

interface CampaignAnalytics {
  tracking_data: TrackingEvent[];
  statistics: CampaignStatistics;
  campaign_metadata?: CampaignMetadata;
}

interface StatCard {
  label: string;
  value: number;
  percentage: number;
  icon: string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

@Component({
  selector: 'app-email-campaign-anlaytics',
  templateUrl: './email-campaign-anlaytics.component.html',
  styleUrls: ['./email-campaign-anlaytics.component.scss']
})
export class EmailCampaignAnlayticsComponent implements OnInit {
  isLoading = true;
  campaignId: string = '';
  
  // Data
  analyticsData: CampaignAnalytics | null = null;
  statistics: CampaignStatistics | null = null;
  trackingEvents: TrackingEvent[] = [];
  campaignMetadata: CampaignMetadata | null = null;
  
  // Computed Stats
  statCards: StatCard[] = [];

  // Active main tab
  activeTab: 'timeline' | 'interested' | 'not_interested' | 'engaged_users' | 'no_action' = 'timeline';
  
  // Filters
  selectedEventType: string = 'all';
  eventTypes: string[] = ['all', 'Send', 'Delivery', 'Open', 'Click', 'Bounce', 'Complaint'];
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 1;

  // Charts
  timelineChartInstance: any = null;
  funnelChartInstance: any = null;
  donutChartInstance: any = null;
  timelineChartSubtitle = '';

  private readonly timelineMetrics = ['Send', 'Delivery', 'Open', 'Click', 'Bounce'];
  private readonly timelineColors: Record<string, string> = {
    Send: '#0D4259',
    Delivery: '#10b981',
    Open: '#3b82f6',
    Click: '#8b5cf6',
    Bounce: '#f59e0b',
    Complaint: '#ef4444',
    Reject: '#64748b'
  };
  private readonly timelineLabels: Record<string, string> = {
    Send: 'Sent',
    Delivery: 'Delivered',
    Open: 'Opened',
    Click: 'Clicked',
    Bounce: 'Bounced',
    Complaint: 'Complained',
    Reject: 'Rejected'
  };

  // ── Precomputed audience email lists ─────────────────────────────────────
  interestedEmailsList: string[] = [];
  notInterestedEmailsList: string[] = [];
  engagedUserEmailsList: string[] = [];
  noActionEmailsList: string[] = [];
  // Maps email → recipient name (populated from Send events that carry email_to_name)
  emailNameMap: Map<string, string> = new Map();

  // ── Create-group modal state ────────────────────────────────────────────
  showGroupModal = false;
  activeGroupModalTab: 'interested' | 'not_interested' | 'engaged_users' | 'no_action' | null = null;
  groupModalInput = '';
  groupModalCreating = false;
  groupModalSuccess = '';
  groupModalError = '';

  // kept for backward-compat with createGroupFromList (legacy per-tab state, no longer shown in UI)
  interestedGroupName = '';
  notInterestedGroupName = '';
  engagedGroupName = '';
  noActionGroupName = '';
  creatingInterestedGroup = false;
  creatingNotInterestedGroup = false;
  creatingEngagedGroup = false;
  creatingNoActionGroup = false;
  interestedGroupSuccess = '';
  notInterestedGroupSuccess = '';
  engagedGroupSuccess = '';
  noActionGroupSuccess = '';
  interestedGroupError = '';
  notInterestedGroupError = '';
  engagedGroupError = '';
  noActionGroupError = '';

  constructor(
    private campaignService: CampaignService,
    private contactGroupService: ContactGrouopService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.params['id'];
    this.getCampaignTracking();
  }

  getCampaignTracking() {
    this.isLoading = true;
    const data = {
      client_id: this.localStorageService.getClientId(),
      campaign_id: this.campaignId
    };

    this.campaignService.getCampaignTracking(data).subscribe({
      next: (res: any) => {
        console.log('Campaign Analytics:', res);
        this.analyticsData = res;
        this.statistics = res.statistics;
        this.trackingEvents = res.tracking_data || [];
        this.campaignMetadata = res.campaign_metadata || null;
        this.buildAudienceLists();
        this.calculateStatCards();
        this.updatePagination();
        this.isLoading = false;

        // Initialize Dashboards Charts
        setTimeout(() => {
          this.initAllCharts();
        }, 100);
      },
      error: (err) => {
        console.error('Error fetching campaign analytics:', err);
        this.isLoading = false;
      }
    });
  }

  calculateStatCards() {
    if (!this.statistics) return;

    // Safe divide helper — returns 0 when divisor is 0/null/NaN instead of 100
    const safePct = (numerator: number, divisor: number): number =>
      divisor > 0 ? (numerator / divisor) * 100 : 0;

    const sent           = this.statistics.sent      || 0;
    const delivered      = this.statistics.delivered  || 0;
    const opened         = this.statistics.opened     || 0;
    const total          = this.statistics.total      || 0;
    const interested     = this.statistics.interested_count     || 0;
    const notInterested  = this.statistics.not_interested_count || 0;
    const engaged        = this.statistics.engaged_count        || 0;

    this.statCards = [
      {
        label: 'Total Sent',
        value: sent,
        percentage: safePct(sent, total),
        icon: 'bi-send-fill',
        color: '#0D4259',
        trend: 'neutral'
      },
      {
        label: 'Delivered',
        value: delivered,
        percentage: safePct(delivered, sent),
        icon: 'bi-check-circle-fill',
        color: '#10b981',
        trend: 'up'
      },
      {
        label: 'Opened',
        value: opened,
        percentage: safePct(opened, delivered),
        icon: 'bi-envelope-open-fill',
        color: '#3b82f6',
        trend: 'up'
      },
      {
        label: 'Clicked',
        value: this.statistics.clicked,
        percentage: safePct(this.statistics.clicked, delivered),
        icon: 'bi-cursor-fill',
        color: '#8b5cf6',
        trend: 'neutral'
      },
      {
        label: 'Bounced',
        value: this.statistics.bounced,
        percentage: safePct(this.statistics.bounced, sent),
        icon: 'bi-exclamation-triangle-fill',
        color: '#f59e0b',
        trend: 'down'
      },
      {
        label: 'Complained',
        value: this.statistics.complained,
        percentage: safePct(this.statistics.complained, delivered),
        icon: 'bi-flag-fill',
        color: '#ef4444',
        trend: 'down'
      },
      // Interest stat cards
      {
        label: 'Interested',
        value: interested,
        percentage: safePct(interested, delivered),
        icon: 'bi-hand-thumbs-up-fill',
        color: '#22c55e',
        trend: 'up'
      },
      {
        label: 'Not Interested',
        value: notInterested,
        percentage: safePct(notInterested, delivered),
        icon: 'bi-hand-thumbs-down-fill',
        color: '#9ca3af',
        trend: 'neutral'
      },
      {
        label: 'Engaged',
        value: engaged,
        percentage: safePct(engaged, delivered),
        icon: 'bi-lightning-charge-fill',
        color: '#3b82f6',
        trend: 'up'
      }
    ];
  }

  // ── Tab navigation ──────────────────────────────────────────────────────────

  setActiveTab(tab: 'timeline' | 'interested' | 'not_interested' | 'engaged_users' | 'no_action') {
    this.activeTab = tab;
  }

  // ── Build audience lists once after trackingEvents is set ───────────────

  buildAudienceLists(): void {
    const interested    = new Set<string>();
    const notInterested = new Set<string>();
    const engaged       = new Set<string>();
    const delivered     = new Set<string>();
    const anyAction     = new Set<string>();
    const nameMap       = new Map<string, string>();

    for (const e of this.trackingEvents) {
      const email = (e.email_to || '').toLowerCase().trim();
      if (!email) continue;
      // Build email → name lookup from any event that carries a name
      if (e.email_to_name && !nameMap.has(email)) {
        nameMap.set(email, e.email_to_name);
      }
      if (e.event_type === 'Interested_Click')     { interested.add(email);    anyAction.add(email); }
      if (e.event_type === 'Not_Interested_Click') { notInterested.add(email); anyAction.add(email); }
      if (e.event_type === 'Open' || e.event_type === 'Click') { engaged.add(email); anyAction.add(email); }
      if (e.event_type === 'Send' || e.event_type === 'Delivery') delivered.add(email);
    }

    this.emailNameMap            = nameMap;
    this.interestedEmailsList    = Array.from(interested);
    this.notInterestedEmailsList = Array.from(notInterested);
    this.engagedUserEmailsList   = Array.from(engaged);
    // No-action: received the email but never opened, clicked, or responded to CTA
    this.noActionEmailsList = Array.from(delivered).filter(e => !anyAction.has(e));
  }

  // ── Create-group modal helpers ────────────────────────────────────────────

  get modalEmails(): string[] {
    switch (this.activeGroupModalTab) {
      case 'interested':     return this.interestedEmailsList;
      case 'not_interested': return this.notInterestedEmailsList;
      case 'engaged_users':  return this.engagedUserEmailsList;
      case 'no_action':      return this.noActionEmailsList;
      default:               return [];
    }
  }

  get modalTitle(): string {
    switch (this.activeGroupModalTab) {
      case 'interested':     return 'Interested Users';
      case 'not_interested': return 'Not Interested Users';
      case 'engaged_users':  return 'Engaged Users';
      case 'no_action':      return 'No Action Users';
      default:               return '';
    }
  }

  openGroupModal(tab: 'interested' | 'not_interested' | 'engaged_users' | 'no_action') {
    this.activeGroupModalTab = tab;
    this.groupModalInput = '';
    this.groupModalSuccess = '';
    this.groupModalError = '';
    this.showGroupModal = true;
  }

  closeGroupModal() {
    if (this.groupModalCreating) return;
    this.showGroupModal = false;
    this.activeGroupModalTab = null;
  }

  submitGroupModal() {
    if (!this.activeGroupModalTab || !this.groupModalInput.trim() || this.groupModalCreating) return;

    const emails    = this.modalEmails;
    const groupName = this.groupModalInput.trim();
    const clientId  = this.localStorageService.getClientId();

    this.groupModalCreating = true;
    this.groupModalSuccess  = '';
    this.groupModalError    = '';

    this.contactGroupService.createContact_group({
      name: groupName,
      client_id: clientId,
      group_type: 'email',
      total_contacts: emails.length,
      status: 'active'
    }).subscribe({
      next: (groupRes: any) => {
        const groupId = groupRes?.id;
        if (!groupId) {
          this.groupModalError    = 'Failed to create group. Please try again.';
          this.groupModalCreating = false;
          return;
        }
        const contacts = emails.map(email => ({
          email,
          name: this.contactNameForGroup(email),
          source: 'campaign'
        }));
        this.contactGroupService.createContactGroupList({
          contact_group_id: groupId,
          client_id: clientId,
          contacts
        }).subscribe({
          next: () => {
            this.groupModalSuccess  = `Group "${groupName}" created with ${emails.length} contact(s)!`;
            this.groupModalInput    = '';
            this.groupModalCreating = false;
            setTimeout(() => this.closeGroupModal(), 2200);
          },
          error: (err: any) => {
            this.groupModalError    = err?.error?.message || 'Failed to add contacts to group.';
            this.groupModalCreating = false;
          }
        });
      },
      error: (err: any) => {
        this.groupModalError    = err?.error?.message || 'Failed to create contact group.';
        this.groupModalCreating = false;
      }
    });
  }

  hasActivity(email: string, eventType: string): boolean {
    return this.trackingEvents.some(
      e => (e.email_to || '').toLowerCase() === email && e.event_type === eventType
    );
  }

  recipientName(email: string): string {
    const name = this.emailNameMap.get(email);
    if (!name || !name.trim()) return '—';
    const firstName = name.trim().split(/\s+/)[0];
    if (!firstName || firstName.toLowerCase() === email.toLowerCase()) return '—';
    return firstName;
  }

  /** First name for contact group, or email when name is unavailable. */
  contactNameForGroup(email: string): string {
    const display = this.recipientName(email);
    return display !== '—' ? display : email;
  }

  // ── Create contact group from an audience list ────────────────────────────

  createGroupFromList(
    emails: string[],
    groupName: string,
    tab: 'interested' | 'not_interested' | 'engaged_users' | 'no_action'
  ) {
    if (!groupName.trim()) return;
    if (emails.length === 0) return;

    const setLoading  = (v: boolean) => { if (tab === 'interested') this.creatingInterestedGroup = v; else if (tab === 'not_interested') this.creatingNotInterestedGroup = v; else if (tab === 'no_action') this.creatingNoActionGroup = v; else this.creatingEngagedGroup = v; };
    const setSuccess  = (v: string)  => { if (tab === 'interested') this.interestedGroupSuccess = v; else if (tab === 'not_interested') this.notInterestedGroupSuccess = v; else if (tab === 'no_action') this.noActionGroupSuccess = v; else this.engagedGroupSuccess = v; };
    const setError    = (v: string)  => { if (tab === 'interested') this.interestedGroupError = v; else if (tab === 'not_interested') this.notInterestedGroupError = v; else if (tab === 'no_action') this.noActionGroupError = v; else this.engagedGroupError = v; };
    const clearName   = ()           => { if (tab === 'interested') this.interestedGroupName = ''; else if (tab === 'not_interested') this.notInterestedGroupName = ''; else if (tab === 'no_action') this.noActionGroupName = ''; else this.engagedGroupName = ''; };

    setLoading(true);
    setSuccess('');
    setError('');

    const clientId = this.localStorageService.getClientId();

    this.contactGroupService.createContact_group({
      name: groupName.trim(),
      client_id: clientId,
      group_type: 'email',
      total_contacts: emails.length,
      status: 'active'
    }).subscribe({
      next: (groupRes: any) => {
        const groupId = groupRes?.id;
        if (!groupId) {
          setError('Failed to create group. Please try again.');
          setLoading(false);
          return;
        }

        const contacts = emails.map(email => ({
          email,
          name: this.contactNameForGroup(email),
          source: 'campaign'
        }));

        this.contactGroupService.createContactGroupList({
          contact_group_id: groupId,
          client_id: clientId,
          contacts
        }).subscribe({
          next: () => {
            setSuccess(`Group "${groupName.trim()}" created with ${emails.length} contact(s)!`);
            clearName();
            setLoading(false);
            setTimeout(() => setSuccess(''), 5000);
          },
          error: (err: any) => {
            setError(err?.error?.message || 'Failed to add contacts to group.');
            setLoading(false);
          }
        });
      },
      error: (err: any) => {
        setError(err?.error?.message || 'Failed to create contact group.');
        setLoading(false);
      }
    });
  }

  initAllCharts() {
    if (!this.statistics) return;
    this.prepareTimelineData();
    this.initFunnelChart();
    this.initDonutChart();
  }

  initFunnelChart() {
    if (this.funnelChartInstance) this.funnelChartInstance.destroy();

    const canvas = document.getElementById('funnelChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const funnelValues = [
      this.statistics?.sent || 0,
      this.statistics?.delivered || 0,
      this.statistics?.opened || 0,
      this.statistics?.clicked || 0
    ];
    const funnelColors = ['#0D4259', '#10b981', '#3b82f6', '#8b5cf6'];

    this.funnelChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Sent', 'Delivered', 'Opened', 'Clicked'],
        datasets: [{
          label: 'Events',
          data: funnelValues,
          backgroundColor: funnelColors.map(color => `${color}CC`),
          borderColor: funnelColors,
          borderWidth: 1,
          borderRadius: 6,
          barPercentage: 0.55
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 28 } },
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            titleColor: '#1e293b',
            bodyColor: '#475569',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: 10,
            callbacks: {
              label: (ctx) => ` ${ctx.parsed.x} events`
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: '#f1f5f9' },
            ticks: { stepSize: 1, precision: 0, color: '#94a3b8' },
            border: { display: false }
          },
          y: {
            grid: { display: false },
            ticks: { color: '#475569', font: { size: 12, weight: 600 } },
            border: { display: false }
          }
        }
      }
    });
  }

  private getEmailDeliveryStatusData(): { labels: string[]; values: number[]; colors: string[] } {
    const delivered = this.statistics?.delivered || 0;
    const bounced = this.statistics?.bounced || 0;
    const complained = this.statistics?.complained || 0;

    return {
      labels: ['Delivered', 'Bounced', 'Complained'],
      values: [delivered, bounced, complained],
      colors: ['#10b981', '#f59e0b', '#ef4444']
    };
  }

  initDonutChart() {
    if (this.donutChartInstance) this.donutChartInstance.destroy();

    const legendHost = document.getElementById('donutChartLegend');
    if (legendHost) legendHost.innerHTML = '';

    const canvas = document.getElementById('donutChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { labels, values, colors } = this.getEmailDeliveryStatusData();

    this.donutChartInstance = new Chart(ctx, {
      type: 'doughnut',
      plugins: [createCampaignDonutLegendPlugin('donutChartLegend', 'donutChart')],
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderWidth: 1,
          hoverOffset: 4
        }]
      },
      options: {
        ...campaignDonutChartOptions,
        plugins: {
          ...campaignDonutChartOptions.plugins,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const value = ctx.parsed || 0;
                const total = values.reduce((sum, n) => sum + n, 0);
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return ` ${ctx.label}: ${value} (${pct}%)`;
              }
            }
          }
        }
      }
    });
  }

  private buildTimelineBuckets(events: TrackingEvent[]): {
    labels: string[];
    getBucketKey: (timestamp: string) => string;
  } {
    if (!events.length) {
      return { labels: [], getBucketKey: () => '' };
    }

    const sorted = [...events].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    const start = new Date(sorted[0].timestamp);
    const end = new Date(sorted[sorted.length - 1].timestamp);
    const sameDay = start.toDateString() === end.toDateString();

    if (sameDay) {
      const labels: string[] = [];
      const cursor = new Date(start);
      cursor.setMinutes(0, 0, 0);
      const endHour = new Date(end);
      endHour.setMinutes(59, 59, 999);

      while (cursor <= endHour) {
        labels.push(cursor.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }));
        cursor.setHours(cursor.getHours() + 1);
      }

      const getBucketKey = (timestamp: string) => {
        const date = new Date(timestamp);
        date.setMinutes(0, 0, 0);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      };

      return { labels, getBucketKey };
    }

    const labels: string[] = [];
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);

    while (cursor <= endDay) {
      labels.push(cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      cursor.setDate(cursor.getDate() + 1);
    }

    const getBucketKey = (timestamp: string) =>
      new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    return { labels, getBucketKey };
  }

  prepareTimelineData() {
    if (!this.trackingEvents.length) return;

    if (this.timelineChartInstance) {
      this.timelineChartInstance.destroy();
    }

    const filtered = this.filteredEvents;
    const sourceEvents = filtered.length ? filtered : this.trackingEvents;
    const isAllEvents = this.selectedEventType === 'all';
    const { labels: timeLabels, getBucketKey } = this.buildTimelineBuckets(sourceEvents);

    if (isAllEvents && timeLabels.length <= 2) {
      const summaryLabels = this.timelineMetrics.map(metric => this.timelineLabels[metric]);
      const summaryData = this.timelineMetrics.map(metric =>
        sourceEvents.filter(e => e.event_type.toLowerCase() === metric.toLowerCase()).length
      );

      this.timelineChartSubtitle = 'Grouped by event status — clearer view for campaigns with few events';
      this.initTimelineChart(
        summaryLabels,
        [{
          label: 'Events',
          data: summaryData,
          backgroundColor: this.timelineMetrics.map(metric => `${this.timelineColors[metric]}CC`),
          borderColor: this.timelineMetrics.map(metric => this.timelineColors[metric]),
          borderWidth: 1,
          borderRadius: 8,
          barPercentage: 0.62
        }],
        false,
        'Event status'
      );
      return;
    }

    this.timelineChartSubtitle = timeLabels.length === 1
      ? 'All events occurred in the same time period'
      : '';

    if (isAllEvents) {
      const datasets = this.timelineMetrics.map(metric => ({
        label: this.timelineLabels[metric],
        data: timeLabels.map(label =>
          sourceEvents.filter(
            e => getBucketKey(e.timestamp) === label &&
              e.event_type.toLowerCase() === metric.toLowerCase()
          ).length
        ),
        backgroundColor: `${this.timelineColors[metric]}CC`,
        borderColor: this.timelineColors[metric],
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.85,
        categoryPercentage: 0.72
      }));

      this.initTimelineChart(timeLabels, datasets, false, 'Time period');
      return;
    }

    const eventKey = this.selectedEventType.toLowerCase();
    const barColor = this.timelineColors[this.selectedEventType] || this.timelineColors['Send'];

    this.initTimelineChart(
      timeLabels,
      [{
        label: this.timelineLabels[this.selectedEventType] || this.selectedEventType,
        data: timeLabels.map(label =>
          sourceEvents.filter(e => getBucketKey(e.timestamp) === label).length
        ),
        backgroundColor: `${barColor}CC`,
        borderColor: barColor,
        borderWidth: 1,
        borderRadius: 6,
        barPercentage: 0.55
      }],
      false,
      'Time period'
    );
  }

  initTimelineChart(labels: string[], datasets: any[], stacked = false, xAxisTitle = 'Time period') {
    const canvas = document.getElementById('timelineChart') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.timelineChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 12 } },
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: datasets.length > 1,
            position: 'top',
            align: 'start',
            labels: {
              usePointStyle: true,
              pointStyle: 'rectRounded',
              boxWidth: 10,
              padding: 18,
              font: { size: 12, weight: 500 }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            titleColor: '#1e293b',
            bodyColor: '#475569',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: 12,
            boxPadding: 4,
            usePointStyle: true,
            titleFont: { size: 13, weight: 'bold' },
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            stacked,
            suggestedMax: Math.max(...datasets.flatMap(d => d.data as number[]), 1) + 1,
            grid: { color: '#f1f5f9' },
            ticks: { stepSize: 1, precision: 0, color: '#94a3b8' },
            border: { display: false },
            title: {
              display: true,
              text: 'Event count',
              color: '#64748b',
              font: { size: 12, weight: 500 }
            }
          },
          x: {
            stacked,
            grid: { display: false },
            ticks: {
              color: '#475569',
              maxRotation: 45,
              minRotation: 0,
              font: { size: 11, weight: 500 }
            },
            border: { display: false },
            title: {
              display: true,
              text: xAxisTitle,
              color: '#64748b',
              font: { size: 12, weight: 500 }
            }
          }
        }
      }
    });
  }
  // View Mode
  viewMode: 'table' | 'graph' = 'table'; // Default to table

  setViewMode(mode: 'table' | 'graph') {
    this.viewMode = mode;
    if (mode === 'graph') {
      setTimeout(() => {
        this.initAllCharts();
      }, 0);
    }
  }

  // Filter events by type
  get filteredEvents(): TrackingEvent[] {
    if (this.selectedEventType === 'all') {
      return this.trackingEvents;
    }
    return this.trackingEvents.filter(event => 
      event.event_type.toLowerCase() === this.selectedEventType.toLowerCase()
    );
  }

  // Paginated events
  get paginatedEvents(): TrackingEvent[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredEvents.slice(startIndex, endIndex);
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredEvents.length / this.itemsPerPage);
  }
  setEventFilter(eventType: string) {
    this.selectedEventType = eventType;
    this.currentPage = 1;
    this.updatePagination();
    this.prepareTimelineData();
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  goToPage(page: number) {
    this.currentPage = page;
  }

  getEventTypeBadgeClass(eventType: string): string {
    return getCampaignEventTypeBadgeClass(eventType);
  }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'sent': 'badge-info',
      'delivered': 'badge-success',
      'opened': 'badge-primary',
      'clicked': 'badge-purple',
      'bounced': 'badge-warning',
      'complained': 'badge-danger',
      'rejected': 'badge-danger'
    };
    return statusMap[status.toLowerCase()] || 'badge-default';
  }

  getEventIcon(eventType: string): string {
    const iconMap: { [key: string]: string } = {
      'send': 'bi-send-fill',
      'delivery': 'bi-check-circle-fill',
      'open': 'bi-envelope-open-fill',
      'click': 'bi-cursor-fill',
      'bounce': 'bi-exclamation-triangle-fill',
      'complaint': 'bi-flag-fill',
      'reject': 'bi-x-circle-fill'
    };
    return iconMap[eventType.toLowerCase()] || 'bi-info-circle-fill';
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null || isNaN(value) || !isFinite(value)) {
      return '0.0';
    }
    return Math.min(100, Math.max(0, value)).toFixed(1);
  }

  viewRawData(event: TrackingEvent) {
    try {
      const rawData = JSON.parse(event.raw_event_data);
      console.log('Raw Event Data:', rawData);
      alert(JSON.stringify(rawData, null, 2));
    } catch (error) {
      console.error('Error parsing raw event data:', error);
    }
  }

  exportData() {
    const dataStr = JSON.stringify(this.analyticsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign-${this.campaignId}-analytics.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  get dispatchProgress(): number {
    if (!this.campaignMetadata || !this.campaignMetadata.total) return 0;
    return Math.round((this.campaignMetadata.sent / this.campaignMetadata.total) * 100);
  }

  get isPartialDispatch(): boolean {
    return !!this.campaignMetadata && this.campaignMetadata.queued > 0;
  }

  goBack() {
    this.router.navigate(['/main/broadcast-campaigns']);
  }

  refreshData() {
    this.getCampaignTracking();
  }
}
